import React from 'react';
import { Progress, Select, Tag, Tooltip } from 'antd';
import {
  AimOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import CallSubjectChip from './subjects/CallSubjectChip';
import { useCallObjections } from './objections/useCallObjections';
import { SCORE_BANDS, bandOf, describeDelta } from './operatorScore';

const BANDS = SCORE_BANDS;

const SEVERITY_META = {
  critical: { label: 'جدی', color: 'red' },
  major: { label: 'مهم', color: 'orange' },
  minor: { label: 'جزئی', color: 'gold' },
};

const ReasonItem = ({ reason }) => {
  const positive = reason.kind === 'positive';
  const severity = SEVERITY_META[reason.severity];
  return (
    <li className={`rounded-lg border px-3 py-2 min-w-0 ${positive
      ? 'border-emerald-100 bg-emerald-50/60 dark:border-emerald-900/50 dark:bg-emerald-950/20'
      : 'border-amber-100 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20'}`}
    >
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`mt-0.5 ${positive ? 'text-emerald-600' : 'text-amber-600'}`}>
          {positive ? <CheckCircleOutlined /> : <WarningOutlined />}
        </span>
        <span className="custom-font text-sm font-medium text-gray-800 dark:text-gray-100 break-words min-w-0">{reason.title}</span>
        {reason.category_label && <Tag className="custom-font m-0 text-[11px]">{reason.category_label}</Tag>}
        {!positive && severity && <Tag color={severity.color} className="custom-font m-0 text-[11px]">{severity.label}</Tag>}
      </div>
      {reason.evidence && (
        <div className="custom-font text-xs leading-6 text-gray-500 dark:text-gray-400 mt-1 ps-6 break-words">«{reason.evidence}»</div>
      )}
    </li>
  );
};

const DELTA_TONES = {
  up: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800',
  down: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800',
  neutral: 'text-gray-500 bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',
};

const CompareRow = ({ label, value, count, highlight = false }) => {
  const band = value != null ? BANDS[bandOf(Math.round(value))] : null;
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className={`custom-font text-xs w-32 shrink-0 truncate ${highlight ? 'font-bold text-gray-800 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
        {label}
      </div>
      <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden" dir="ltr">
        {value != null && (
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.max(2, Math.min(100, value))}%`, background: `linear-gradient(90deg, ${band.from}, ${band.to})` }}
          />
        )}
      </div>
      <div className={`custom-font text-sm w-20 text-left shrink-0 ${highlight ? 'font-bold' : ''}`} dir="ltr">
        {value != null ? value.toLocaleString('en-US', { maximumFractionDigits: 1 }) : '—'}
        {count != null && value != null && <span className="text-[10px] text-gray-400"> ({count})</span>}
      </div>
    </div>
  );
};

/** مقایسه امتیاز همین تماس با میانگین تیم و میانگین خود اپراتور در N روز منتهی به تماس */
const ScoreComparison = ({ score, comparison }) => {
  if (score == null || !comparison || (comparison.team_avg == null && comparison.agent_avg == null)) return null;
  const teamDelta = describeDelta(comparison.team_delta, 'میانگین تیم');
  const agentDelta = describeDelta(comparison.agent_delta, 'میانگین خود اپراتور');

  return (
    <div className="rounded-lg border border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-900/30 p-3 space-y-2">
      <div className="custom-font text-xs font-medium text-gray-600 dark:text-gray-300">
        مقایسه با {Number(comparison.days).toLocaleString('fa-IR')} روز قبل از این تماس
      </div>
      <CompareRow label="امتیاز این تماس" value={score} highlight />
      <CompareRow label="میانگین تیم" value={comparison.team_avg} count={comparison.team_count} />
      <CompareRow label="میانگین این اپراتور" value={comparison.agent_avg} count={comparison.agent_count} />
      <div className="flex flex-wrap gap-1.5 pt-1">
        {[teamDelta, agentDelta].filter(Boolean).map((item) => (
          <span key={item.text} className={`custom-font text-[11px] rounded-full border px-2 py-0.5 ${DELTA_TONES[item.tone]}`}>
            {item.tone === 'up' ? '▲ ' : item.tone === 'down' ? '▼ ' : '● '}
            {item.text}
          </span>
        ))}
      </div>
      <div className="custom-font text-[10px] text-gray-400">
        عدد داخل پرانتز، تعداد تماس‌های امتیازدار است. خود این تماس در میانگین‌ها حساب نشده است.
      </div>
    </div>
  );
};

/** امتیاز عملکرد اپراتور: حلقه رنگی ۰ تا ۱۰۰ + دلایل (نقاط قوت و نکات قابل بهبود) */
const OperatorScoreCard = ({ score, operator, comparison }) => {
  const bandKey = bandOf(score);
  const band = bandKey ? BANDS[bandKey] : null;
  const reasons = operator?.reasons || [];
  const positives = reasons.filter((r) => r.kind === 'positive');
  const negatives = reasons.filter((r) => r.kind === 'negative');

  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-3 md:p-4">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        <div className="flex flex-col items-center gap-2 shrink-0 md:w-52">
          <Progress
            type="circle"
            percent={score ?? 0}
            size={156}
            strokeWidth={9}
            strokeLinecap="round"
            strokeColor={band ? { '0%': band.from, '100%': band.to } : '#d1d5db'}
            trailColor="rgba(148,163,184,0.2)"
            format={() => (
              <div className="custom-font leading-tight" dir="ltr">
                <div className={`text-4xl font-extrabold ${band ? band.text : 'text-gray-300'}`}>{score ?? '—'}</div>
                <div className="text-xs text-gray-400">از ۱۰۰</div>
              </div>
            )}
          />
          <div className={`custom-font text-sm font-bold ${band ? band.text : 'text-gray-400'}`}>
            {band ? band.label : 'قابل ارزیابی نیست'}
          </div>
          <div className="grid grid-cols-2 gap-1 w-full">
            {Object.entries(BANDS).reverse().map(([key, item]) => (
              <div
                key={key}
                className={`rounded-md px-1.5 py-0.5 text-[10px] text-center custom-font border ${bandKey === key ? 'font-bold' : 'opacity-60'}`}
                style={{ borderColor: item.from, color: item.from, background: bandKey === key ? `${item.from}1f` : 'transparent' }}
              >
                {item.range}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <div className="custom-font text-sm font-bold text-gray-800 dark:text-gray-100">امتیاز عملکرد اپراتور</div>
            <div className="custom-font text-[11px] text-gray-400 leading-5">
              این امتیاز برای بهبود عملکرد، افزایش بهره‌وری و رقابت سالم است؛ نه تنبیه.
            </div>
          </div>

          {operator?.summary && (
            <div className="custom-font text-sm leading-7 text-gray-700 dark:text-gray-200 break-words">{operator.summary}</div>
          )}

          <ScoreComparison score={score} comparison={comparison} />

          {operator?.method === 'weighted' && (operator?.standards || []).length > 0 && (
            <div>
              <div className="custom-font text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                امتیاز بر اساس استانداردهای صف (میانگین وزنی)
              </div>
              <ul className="list-none m-0 p-0 space-y-1.5">
                {operator.standards.map((st) => {
                  const stBand = st.score != null ? BANDS[bandOf(st.score)] : null;
                  return (
                    <li key={st.id} className="rounded-lg border border-gray-100 dark:border-gray-700/60 px-3 py-2">
                      <div className="flex items-center justify-between gap-2 custom-font text-sm">
                        <span className="font-medium text-gray-800 dark:text-gray-100 truncate">{st.name}</span>
                        <span className="shrink-0 text-xs text-gray-400">
                          وزن {st.weight} ·{' '}
                          <b className={stBand ? stBand.text : 'text-gray-400'} dir="ltr">{st.score ?? 'قابل ارزیابی نیست'}</b>
                        </span>
                      </div>
                      <Progress percent={st.score ?? 0} showInfo={false} size="small" strokeColor={stBand?.from} className="m-0" />
                      {st.evidence && <div className="custom-font text-xs text-gray-500 leading-5 break-words">{st.evidence}</div>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {positives.length > 0 && (
            <div>
              <div className="custom-font text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1">نقاط قوت</div>
              <ul className="list-none m-0 p-0 space-y-1.5">{positives.map((r, i) => <ReasonItem key={`p${i}`} reason={r} />)}</ul>
            </div>
          )}
          {negatives.length > 0 && (
            <div>
              <div className="custom-font text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">نکات قابل بهبود</div>
              <ul className="list-none m-0 p-0 space-y-1.5">{negatives.map((r, i) => <ReasonItem key={`n${i}`} reason={r} />)}</ul>
            </div>
          )}
          {score != null && reasons.length === 0 && (
            <div className="custom-font text-xs text-gray-400">دلیل مشخصی برای این امتیاز ثبت نشده است. تحلیل مجدد تماس را اجرا کنید.</div>
          )}
          {operator?.adjusted && (
            <div className="custom-font text-[11px] text-gray-400 leading-5 flex items-start gap-1">
              <InfoCircleOutlined className="mt-1 shrink-0" />
              امتیاز پیشنهادی مدل بر اساس قاعده بازه‌ها تعدیل شد: اشتباه جزئی هرگز زیر ۶۰ نمی‌رود و خطای جدی بالای ۵۹ نمی‌ماند.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** هر شاخص معنای مستقل دارد و عمداً در یک عدد ترکیب نمی‌شود */
const MetricTile = ({ icon, label, hint, children, tone = 'text-gray-900 dark:text-white' }) => (
  <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 px-3 py-2.5 min-w-0">
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 min-w-0">
        {icon}
        <span className="truncate">{label}</span>
      </span>
      <Tooltip title={<span className="custom-font">{hint}</span>}>
        <InfoCircleOutlined className="text-gray-300 hover:text-gray-500 cursor-help shrink-0" />
      </Tooltip>
    </div>
    <div className={`custom-font text-xl font-bold leading-8 ${tone}`} dir="ltr">{children}</div>
  </div>
);

/**
 * ارزیابی تماس: امتیاز اپراتور (همراه دلایل)، FCR، اعتراض‌ها و توضیح Coaching.
 */
const CallInsightsSection = ({ call, savingObjections, onObjectionsChange }) => {
  const { objections: catalog, loading } = useCallObjections();
  const insights = call?.ai_analysis?.crm_insights || null;
  const operator = insights?.operator_score || null;
  const operatorScore = call?.operator_score ?? operator?.score ?? null;
  const fcr = call?.fcr;
  const selected = (call?.objections || []).map((o) => o.id);
  const evidenceById = Object.fromEntries((insights?.objections || []).map((o) => [o.id, o.evidence]));

  return (
    <div className="space-y-3">
      <OperatorScoreCard score={operatorScore} operator={operator} comparison={call?.score_comparison} />

      <div className="grid grid-cols-1 gap-2">
        <MetricTile
          icon={<CheckCircleOutlined />}
          label="FCR (حل در تماس اول)"
          hint="آیا مشکل یا درخواست مشتری در همین تماس کاملاً حل شد؟"
        >
          {fcr == null
            ? <span className="text-gray-300">—</span>
            : <Tag color={fcr ? 'success' : 'error'} className="custom-font m-0 text-sm">{fcr ? 'موفق' : 'ناموفق'}</Tag>}
        </MetricTile>
      </div>
      {insights?.fcr_basis && (
        <div className="text-[11px] text-gray-400 leading-5">مبنای تشخیص FCR: {insights.fcr_basis}</div>
      )}

      <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-900/30 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
          <StopOutlined className="text-amber-500" />
          اعتراض‌های مطرح‌شده توسط مشتری
        </div>
        <Select
          mode="multiple"
          allowClear
          showSearch
          loading={loading || savingObjections}
          disabled={savingObjections}
          value={selected}
          onChange={onObjectionsChange}
          placeholder={catalog.length ? 'انتخاب اعتراض' : 'هنوز اعتراضی تعریف نشده است'}
          optionFilterProp="label"
          options={catalog.map((o) => ({ value: o.id, label: o.name }))}
          className="w-full custom-font"
          tagRender={({ value, onClose }) => {
            const item = catalog.find((o) => o.id === value) || (call.objections || []).find((o) => o.id === value);
            return item ? (
              <span className="me-1 my-0.5 inline-flex">
                <CallSubjectChip subject={{ ...item, description: evidenceById[value] ? `«${evidenceById[value]}»` : undefined }} size="small" />
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClose} className="text-gray-400 hover:text-gray-600 px-1 leading-none" aria-label="حذف">×</button>
              </span>
            ) : null;
          }}
        />
        {selected.length === 0 && (
          <div className="text-[11px] text-gray-400">اعتراضی ثبت نشده است. هوش مصنوعی فقط از میان فهرست تعریف‌شده تشخیص می‌دهد؛ می‌توانید دستی هم اضافه کنید.</div>
        )}
      </div>

      {insights?.subject_reason && call?.subject && (
        <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
          <AimOutlined className="mt-1 shrink-0" />
          <span className="flex items-center gap-1.5 flex-wrap">
            موضوع <CallSubjectChip subject={call.subject} size="small" /> — {insights.subject_reason}
          </span>
        </div>
      )}

      {insights?.coaching_description && (
        <div className="rounded-xl border border-purple-100 dark:border-purple-900/50 bg-purple-50/60 dark:bg-purple-950/20 p-3">
          <div className="flex items-center gap-2 text-xs font-bold text-purple-700 dark:text-purple-300 mb-1">
            <BulbOutlined /> توضیح Coaching برای اپراتور
          </div>
          <div className="custom-font text-sm leading-7 text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
            {insights.coaching_description}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallInsightsSection;
